import React, { ChangeEvent, useRef } from 'react'
import { CheckCircle, ChevronRight, FileText, Info, X } from 'lucide-react'

import { Spinner } from '@rewardops/ui'

import { triggerToast } from '~/utils/trigger-toast'
import { useTranslation } from '~/app/i18n/client'
import { useApi } from '../hooks/use-api'
import { Toast } from '../toast'

type ElpPromotion = {
  title: string
  brand: string
  type?: string
  instanceId: string
  instanceIds: string
  memberOptedIn: boolean
  memberOptInRequired: boolean
  name: string
  promoCode?: string
  promotionDescription: string
  description: string
  termsAndConditions?: string
  startDate: number
  endDate: number
  brandColor?: string
  promoImage: {
    file: {
      url: string
    }
  }
}

type ElpPromotionCardProps = {
  promotion: ElpPromotion
  refetchOffers: () => void
}

const ElpPromotionCard: React.FC<ElpPromotionCardProps> = ({
  promotion,
  refetchOffers,
}: ElpPromotionCardProps) => {
  const [showDetails, setShowDetails] = React.useState<boolean>(false)
  const [promoCode, setPromoCode] = React.useState<string>('')
  const [promoCodeError, setPromoCodeError] = React.useState<string>('')
  const [error, setError] = React.useState<string>('')
  const { t: translate } = useTranslation()
  const { api } = useApi()

  const brandColor = promotion?.brandColor
  const inputRef = useRef<HTMLInputElement | null>(null)

  const optIntoElpPromotionMutation =
    api.elpPromotions.optIntoAPromotion.useMutation({
      onSuccess: () => {
        refetchOffers()
      },
      onError: (err) => {
        triggerToast(
          setError,
          err?.message ?? 'opting into the promotion failed',
        )
      },
    })

  const optIn = () => {
    setError('')

    if (
      promotion?.promoCode &&
      promotion?.promoCode?.toLowerCase() !== promoCode?.trim()?.toLowerCase()
    ) {
      setPromoCodeError('Incorrect promo code')
      inputRef.current?.focus()
      return
    }

    optIntoElpPromotionMutation.mutate({
      instanceIds: promotion?.instanceIds,
    })
  }

  const handlePromoCodeTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPromoCodeError('')
    setPromoCode(e.target.value)
  }

  return (
    <div
      data-card-id={promotion?.instanceIds}
      className="relative h-[510px] w-full rounded-[20px] border border-gray-200 lg:w-[350px]"
    >
      <div className="flex h-full flex-col gap-2">
        <div className="relative h-[167px] rounded-t-[20px]">
          {promotion?.type && (
            <div className="absolute left-4 top-4">
              <div className="rounded-full bg-white px-4 py-1 text-xs font-semibold uppercase">
                {promotion?.type}
              </div>
            </div>
          )}

          {promotion.promoImage?.file?.url ? (
            <img
              src={promotion.promoImage?.file?.url}
              className="h-full w-full rounded-t-[20px] object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-t-[20px] bg-gray-200 text-sm text-gray-500">
              No Image Available
            </div>
          )}

          {promotion?.memberOptedIn && !showDetails && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-t-[20px] bg-black/10 backdrop-blur-sm">
              <div className="rounded-full bg-white p-2">
                <CheckCircle
                  size={32}
                  className={!brandColor ? 'text-tenant-primary-color' : ''}
                  style={brandColor ? { color: brandColor } : undefined}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex h-full flex-col gap-2 p-6">
          <div className="flex justify-between">
            <div
              className={`text-sm font-semibold uppercase tracking-wider ${
                !brandColor ? 'text-tenant-primary-color' : ''
              }`}
              style={brandColor ? { color: brandColor } : undefined}
            >
              {promotion.brand}
            </div>
            <button
              onClick={() => setShowDetails(true)}
              disabled={optIntoElpPromotionMutation?.isLoading}
              className="flex gap-1 text-xs font-semibold uppercase text-gray-400 hover:underline hover:underline-offset-4 disabled:cursor-not-allowed"
            >
              {translate('elpPromotions.viewDetails')}{' '}
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="text-lg font-semibold capitalize">
            {promotion.title}
          </div>

          <div className="max-h-32 overflow-y-auto text-sm text-gray-400">
            {promotion?.description ?? promotion.promotionDescription}
          </div>

          {promotion?.memberOptedIn ? (
            <div className="mt-auto">
              <button
                disabled
                className={`flex w-full items-center justify-center gap-2 rounded-[14px] p-2 text-base font-semibold uppercase tracking-wider ${
                  brandColor
                    ? 'border-0 text-white'
                    : 'border-2 border-tenant-primary-color bg-green-50 text-tenant-primary-color'
                }`}
                style={
                  brandColor
                    ? { backgroundColor: brandColor, color: '#ffffff' }
                    : undefined
                }
              >
                <CheckCircle
                  className={brandColor ? 'text-white' : undefined}
                />
                {translate('elpPromotions.enrolled')}
              </button>
            </div>
          ) : (
            <div className="mt-auto flex flex-col gap-4">
              {promotion?.promoCode && (
                <div className="relative w-full">
                  <input
                    id="enter-elp-promo-code"
                    ref={inputRef}
                    className={`w-full rounded-[14px] border border-gray-200 bg-gray-100 px-4 py-2 pr-10 font-bold uppercase tracking-wider text-gray-400 focus:outline-none focus:ring-2 ${
                      promoCodeError
                        ? 'focus:ring-red-500'
                        : !brandColor
                        ? 'focus:ring-tenant-primary-color'
                        : ''
                    }`}
                    style={
                      !promoCodeError && brandColor
                        ? ({
                            '--tw-ring-color': brandColor,
                          } as React.CSSProperties)
                        : undefined
                    }
                    placeholder={translate('elpPromotions.enterPromoCode')}
                    onChange={handlePromoCodeTextChange}
                  />

                  {promoCodeError && (
                    <div className="absolute right-3 top-1/2 z-50 -translate-y-1/2">
                      <div className="group relative inline-block">
                        <Info className="h-5 w-5 cursor-pointer text-red-500" />

                        <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-max -translate-x-1/2 rounded bg-red-500 px-4 py-2 text-sm capitalize text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          {promoCodeError}
                          <div className="absolute left-1/2 top-full h-0 w-0 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-red-500"></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                disabled={optIntoElpPromotionMutation?.isLoading}
                onClick={optIn}
                className={`flex w-full justify-center gap-2 rounded-[14px] p-2 text-base font-semibold uppercase text-white disabled:bg-gray-400 ${
                  !brandColor ? 'bg-tenant-primary-color' : ''
                }`}
                style={brandColor ? { backgroundColor: brandColor } : undefined}
              >
                {optIntoElpPromotionMutation?.isLoading && (
                  <Spinner size="small" className="text-white" />
                )}
                {translate('elpPromotions.joinPromotion')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className={`absolute inset-0 flex transform flex-col rounded-[20px]
          bg-white transition-transform duration-500 ease-in-out
          ${
            showDetails
              ? 'pointer-events-auto translate-y-0 opacity-100'
              : 'pointer-events-none translate-y-full opacity-0'
          }
          flex h-full flex-col
        `}
      >
        <div className="flex justify-between px-6 py-4">
          <div className="flex items-center justify-center gap-2 text-base font-semibold uppercase tracking-wider text-gray-400">
            <FileText
              size={18}
              className={!brandColor ? 'text-tenant-primary-color' : ''}
              style={brandColor ? { color: brandColor } : undefined}
            />
            {translate('elpPromotions.promoDetailsHeading')}
          </div>
          <button onClick={() => setShowDetails(false)}>
            <X size={18} className="text-gray-400 hover:text-gray-500" />
          </button>
        </div>
        <hr />
        <div className="mt-2 flex h-full max-h-max flex-col gap-4 overflow-y-auto px-6 py-4">
          <div className="text-lg font-semibold capitalize">
            {promotion?.title}
          </div>
          <div
            className={`text-sm font-semibold uppercase tracking-wider ${
              !brandColor ? 'text-tenant-primary-color' : ''
            }`}
            style={brandColor ? { color: brandColor } : undefined}
          >
            {translate('elpPromotions.descriptionHeading')}
          </div>
          <div className="text-sm text-gray-400">
            {promotion?.description ?? promotion.promotionDescription}
          </div>
          {promotion.termsAndConditions && (
            <div className="rounded-[14px] rounded-[20px] border border-gray-200 bg-gray-100 p-4">
              <div className="flex gap-2">
                <Info size={16} className="text-gray-400" />
                <div className="text-sm font-semibold uppercase text-gray-400">
                  {translate('elpPromotions.termsAndConditionsHeading')}
                </div>
              </div>
              <div className="mt-2 text-sm italic text-gray-400">
                {promotion.termsAndConditions}
              </div>
            </div>
          )}
        </div>
        <div className="mt-auto px-6 py-4">
          <button
            onClick={() => setShowDetails(false)}
            className={`w-full rounded-[14px] p-2 text-base font-semibold uppercase text-white ${
              !brandColor ? 'bg-tenant-primary-color' : ''
            }`}
            style={brandColor ? { backgroundColor: brandColor } : undefined}
          >
            {translate('elpPromotions.backToRegister')}
          </button>
        </div>
      </div>

      {error ? <Toast message={error} /> : null}
    </div>
  )
}

export { ElpPromotionCard }
